import React, { useCallback, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import { useDispatch } from "react-redux";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../../../redux/loader/loaderActions";
import baseUrl from "../../../../../api/baseUrl";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";

type PaperSize = "A4" | "A5";

const REPORT_SECTIONS = [
  { KEY: "1", label: "Patient complain" },
  { KEY: "2", label: "Investigation" },
  { KEY: "3", label: "Diagnosis" },
  { KEY: "4", label: "treatments" },
  { KEY: "5", label: "Payments" },
];

const PAPER_SIZES: { value: PaperSize; label: string; hint: string }[] = [
  { value: "A4", label: "A4", hint: "210 × 297 mm" },
  { value: "A5", label: "A5", hint: "148 × 210 mm" },
];

const PAPER_SIZE_STORAGE_KEY = "reportPaperSize";

function ReportModal({ setReportOpen }: any) {
  const dispatch = useDispatch();
  const { ID } = useParams();
  const [formData, setFormData] = useState<string[]>(["1"]);
  const [paperSize, setPaperSize] = useState<PaperSize>("A4");

  useEffect(() => {
    const storedSections = localStorage.getItem("reportData");
    if (storedSections) {
      try {
        const parsed = JSON.parse(storedSections);
        if (Array.isArray(parsed) && parsed.length) {
          setFormData(parsed.map(String));
        }
      } catch {
        /* ignore corrupt storage */
      }
    }

    const storedSize = localStorage.getItem(PAPER_SIZE_STORAGE_KEY);
    if (storedSize === "A4" || storedSize === "A5") {
      setPaperSize(storedSize);
    }
  }, []);

  const handleSectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    const next = checked
      ? [...formData, value]
      : formData.filter((key) => key !== value);
    setFormData(next);
    localStorage.setItem("reportData", JSON.stringify(next));
  };

  const handlePaperSizeChange = (size: PaperSize) => {
    setPaperSize(size);
    localStorage.setItem(PAPER_SIZE_STORAGE_KEY, size);
  };

  const generate = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!formData.length) {
        toast.error("Select at least one section");
        return;
      }
      dispatch(setloadeActive());
      baseUrl
        .get("/client/report/patientReport", {
          responseType: "blob",
          headers: {
            treatmentid: ID,
            data: formData,
            pagesize: paperSize,
          },
        })
        .then((response) => {
          if (response.status === 204) {
            toast("No data found");
            return;
          }
          const pdfBlob = new Blob([response.data], {
            type: "application/pdf",
          });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(pdfBlob);
          link.download = `patient-report-${paperSize.toLowerCase()}.pdf`;
          document.body.appendChild(link);
          link.click();
          URL.revokeObjectURL(link.href);
          document.body.removeChild(link);
        })
        .catch(console.error)
        .finally(() => dispatch(setloadeDeactive()));
    },
    [dispatch, formData, ID, paperSize]
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-gradient-to-r from-primary to-primaryLight">
          <div className="flex items-center gap-3">
            <DescriptionIcon className="text-white" />
            <h2 className="text-xl font-bold text-white">Generate Report</h2>
          </div>
          <button
            type="button"
            onClick={() => setReportOpen(false)}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        <form onSubmit={generate} className="p-6 space-y-4">
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Paper size
            </p>
            <div className="grid grid-cols-2 gap-2">
              {PAPER_SIZES.map((size) => {
                const selected = paperSize === size.value;
                return (
                  <button
                    key={size.value}
                    type="button"
                    onClick={() => handlePaperSizeChange(size.value)}
                    className={`p-3 rounded-xl text-left transition-all border-2 ${
                      selected
                        ? "bg-indigo-50 dark:bg-primary/20 border-indigo-500 dark:border-primary"
                        : "bg-slate-50 dark:bg-slate-700/50 border-transparent hover:bg-slate-100 dark:hover:bg-slate-700"
                    }`}
                  >
                    <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {size.label}
                    </span>
                    <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {size.hint}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Sections to include
            </p>
            <div className="space-y-2">
              {REPORT_SECTIONS.map((item) => {
                const checked = formData.includes(item.KEY);
                return (
                  <label
                    key={item.KEY}
                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                      checked
                        ? "bg-indigo-50 dark:bg-primary/20 border-2 border-indigo-500 dark:border-primary"
                        : "bg-slate-50 dark:bg-slate-700/50 border-2 border-transparent hover:bg-slate-100 dark:hover:bg-slate-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      onChange={handleSectionChange}
                      name={item.label}
                      value={item.KEY}
                      checked={checked}
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 rounded-md flex items-center justify-center ${
                        checked
                          ? "bg-indigo-500 dark:bg-primary text-white"
                          : "bg-white dark:bg-slate-600 border-2 border-slate-300 dark:border-slate-500"
                      }`}
                    >
                      {checked && (
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {item.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            className="w-full mt-2 py-3 rounded-xl bg-gradient-to-r from-primary to-primaryLight text-white font-medium hover:shadow-lg hover:shadow-primary/20 transition-all"
          >
            Generate Report ({paperSize})
          </button>
        </form>
      </div>
    </div>
  );
}

export default ReportModal;
